import type { ReactNode } from 'react';

/**
 * The strip an active flow row expands into: context on the left, the move on
 * the right.
 *
 * ── Why it still pairs, and no longer boxes ──
 *
 * A move never arrives naked: it comes with the thing it acts on — the carrier
 * and tracking number beside "Mark as delivered", the invoice beside "Pay" —
 * because a button alone asks the reader to remember what they are confirming,
 * and the row above only names the step. That pairing is the point and it has
 * not changed.
 *
 * What changed is the container: there isn't one. It was a bordered warn-tinted
 * panel, then an accent rule, and both spoke louder than the sample stepper
 * below them — which states the same idea with a label and a button on one line.
 * One card should not hold two grammars for "here is something to do".
 *
 * ── `mine` ──
 *
 * Accepted and unused. It drove the tint, which was doing ownership's job before
 * `waitingOn` existed; now every section head carries an accurate chip and a
 * second signal inside the row only repeats it. Kept on the type so callers need
 * not change, and so this note sits where someone would otherwise re-add it.
 *
 * A strip that is NOT the viewer's still renders — "invoice sent, waiting on
 * payment" is the answer to why the row is sitting still, and it is worth as
 * much as the button would be.
 */
export function ActionBox({ children }: { mine?: boolean; children: ReactNode }) {
	// No box, no rule, no tint — a row.
	//
	// This was a bordered warn-tinted panel, then an accent rule. Both were
	// answering "here is something to do" in a heavier voice than the sample
	// stepper directly beneath it, which says the same thing with a label and a
	// button on one line. Two grammars for one idea on one card.
	//
	// `mine` is still accepted and deliberately unused: every section head now
	// carries an accurate `waitingOn` chip, so a second ownership signal inside
	// the row is redundant. Kept on the type so callers need not change and so
	// the reason survives where someone would otherwise re-add a tint.
	return <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">{children}</div>;
}

/**
 * The sentence inside an ActionBox — what this move is about.
 *
 * Takes the flexible width so the buttons stay right-aligned and the box
 * collapses to a stack on a narrow column rather than squeezing the label.
 */
export function ActionContext({ children }: { children: ReactNode }) {
	return <span className="min-w-0 flex-[1_1_150px] text-xs text-text-2">{children}</span>;
}
